import { useTranslation } from 'react-i18next'

import { authoredText } from '@/services/authoredText'
import { useClubRestrictionBanners } from '@/services/clubRestrictions'

/** Where this app keeps the phrases the server names for restrictions. */
const RESTRICTION_NAMESPACE = 'club.'

const SEVERITY_STYLE: Record<string, string> = {
  INFO: 'border-gray-300 text-gray-700',
  WARNING: 'border-boldOrange text-boldOrange',
  CRITICAL: 'border-red-500 text-red-500',
}

type Props = {
  clubId: string | number | undefined
  /** The restriction URNs the club payload carried FOR THIS READER. */
  urns: string[]
  /** A director or organizer of this club, who is shown the staff wording instead. */
  isStaff: boolean
}

/**
 * What this reader is told about the club's restrictions.
 *
 * The club payload decides WHICH restrictions a reader learns about — staff see all of them, and
 * everyone else sees only the ones whose type has public wording, and only where the club's
 * disclosure is switched on — so this renders whatever it is given rather than deciding anything
 * itself. Nothing is drawn when there is nothing to say.
 */
export const ClubRestrictionBanners = ({ clubId, urns, isStaff }: Props) => {
  const { t, i18n } = useTranslation()
  const { banners } = useClubRestrictionBanners(clubId, urns, isStaff)

  if (banners.length === 0) return null

  return (
    <div className="mt-4 flex w-full flex-col gap-2">
      {banners.map((banner) => (
        <p
          key={banner.urn}
          className={`w-full rounded-lg border-2 px-3 py-2 text-sm font-semibold ${
            SEVERITY_STYLE[banner.severity] ?? SEVERITY_STYLE.WARNING
          }`}
        >
          {authoredText({ t, exists: i18n.exists.bind(i18n) }, banner.text, RESTRICTION_NAMESPACE)}
        </p>
      ))}
    </div>
  )
}
